import React, { useState, useEffect, useContext, useCallback } from "react";
import ScheduleCard from "components/schedule-card";
import BottomBar from "components/bottom-bar";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Reschedule from "../../components/appointments/rescheduler";
import Extend from "../../components/appointments/extend";
import advancedFormat from "dayjs/plugin/advancedFormat.js";
import AuthContext from "contexts/AuthContext";

import { getAppointments } from "../../components/appointments/service";
import { useMyTakebacks } from "../../api/use-takebacks";

// Extend dayjs with plugins
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const buildHalfHours = () =>
  Array.from({ length: 19 }, (_, i) => {
    const hour = Math.floor(i / 2) + 10;
    const minutes = i % 2 === 0 ? `00` : `30`;

    return [hour, minutes];
  }).filter((time) => {
    const [hour] = time;

    return hour <= 19;
  });

const getTimezoneForState = (state) => {
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };
  return timezoneMap[state] || dayjs.tz.guess();
};

const formatAppointmentTime = (time, state) => {
  const timezone = getTimezoneForState(state);
  const hasStateTimezone = ["CO", "AR", "NJ", "PA"].some((item) =>
    state.toUpperCase().includes(item)
  );

  if (hasStateTimezone) {
    return dayjs(time).tz(timezone).format("h:mm A");
  } else {
    return dayjs(time).format("h:mm A");
  }
};

const formatSlotTime = (slotTime, state) => {
  const timezone = getTimezoneForState(state);
  const hasStateTimezone = ["CO", "AR", "NJ", "PA"].some((item) =>
    state.toUpperCase().includes(item)
  );

  if (hasStateTimezone) {
    return slotTime.tz(timezone).format("h:mm A");
  } else {
    return slotTime.format("h:mm A");
  }
};

export default function Schedule() {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day"));
  const [rescheduledAppointment, setRescheduledAppointment] = useState(null);
  const [extendedAppointment, setExtendedAppointment] = useState(null);
  const [currentLocation, setCurrentLocation] = useState({ state: "" });
  const { user } = useContext(AuthContext);
  // Fetch all takebacks using the existing hook
  const { data: takebacks } = useMyTakebacks({
    offset: 0,
  });

  const fetchAndJoinData = useCallback(async () => {
    // get user's email
    if (user.state) {
      setCurrentLocation({ state: user.state });
    }
    try {
      const appointmentsData = await getAppointments(user.state);
      const confirmedAppointments = appointmentsData.filter(
        (appointment) =>
          appointment.status === "confirmed" || appointment.status === "no-show"
      );

      // If we have takebacks data, join them with appointments
      if (takebacks && takebacks.length > 0) {
        const appointmentsWithTakebacks = confirmedAppointments.map(
          (appointment) => {
            // Find matching takeback based on appointment.sys_id === takeback.id
            const matchingTakeback = takebacks.find((takeback) => {
              return takeback.number === appointment.sys_id;
            });

            return {
              ...appointment,
              takeback: matchingTakeback || null,
            };
          }
        );

        setAppointments(appointmentsWithTakebacks);
      } else {
        // If no takebacks data yet, just set appointments without takeback info
        setAppointments(confirmedAppointments);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      setAppointments([]);
    }
  }, [takebacks, user.state]);

  useEffect(() => {
    fetchAndJoinData();
  }, [fetchAndJoinData]);

  const navigateDate = (direction) => {
    const newDate =
      direction === "next"
        ? selectedDate.add(1, "day")
        : selectedDate.subtract(1, "day");

    // // Don't allow navigating to dates before today
    // if (
    //   direction === "prev" &&
    //   newDate.isBefore(dayjs().startOf("day"), "day")
    // ) {
    //   return;
    // }

    setSelectedDate(newDate);
  };

  const formatShortDate = (date) => {
    return date.format("ddd, D");
  };

  return (
    <div className="flex flex-col px-6 py-4 items-start min-h-full">
      <div className="mb-4">
        <h1 className="text-6xl pb-2 font-bold">Appointments</h1>
      </div>
      <div className="flex items-center justify-between w-full mb-8">
        <button
          onClick={() => navigateDate("prev")}
          className={`text-[#006CB7]`}
        >
          <span className="mr-2">←</span>
          {formatShortDate(selectedDate.subtract(1, "day"))}
        </button>

        <h2 className="text-2xl font-semibold">
          <div className="flex items-center">
            <div className="text-black text-3xl font-bold">
              {selectedDate.format("MMMM Do YYYY")}
            </div>
          </div>

          {/* {formatDate(selectedDate)} */}
        </h2>

        <button onClick={() => navigateDate("next")} className="text-[#006CB7]">
          {formatShortDate(selectedDate.add(1, "day"))}
          <span className="ml-2">→</span>
        </button>
      </div>

      <div className="flex flex-col w-full">
        <section className="flex flex-col flex-1">
          <div className="grid w-full gap-large pb-20">
            {/* Generate time slots for every hour from 10am to 7pm (schedule view) */}
            {buildHalfHours().map(([hour, mins]) => {
              const timezone = getTimezoneForState(currentLocation.state);
              const hasStateTimezone =
                currentLocation && currentLocation.state
                  ? ["CO", "AR", "NJ", "PA"].some((item) =>
                      currentLocation.state.toUpperCase().includes(item)
                    )
                  : false;

              let slotTime;
              if (hasStateTimezone) {
                // Create the time in the target timezone while preserving the selected date
                slotTime = dayjs
                  .tz(selectedDate.format("YYYY-MM-DD"), timezone)
                  .hour(hour)
                  .minute(mins)
                  .second(0);
              } else {
                slotTime = dayjs(selectedDate)
                  .hour(hour)
                  .minute(mins)
                  .second(0);
              }

              // Find all appointments for this hour
              const slotAppointments = appointments.filter((appointment) => {
                const timezone = getTimezoneForState(currentLocation.state);
                const hasStateTimezone =
                  currentLocation && currentLocation.state
                    ? ["CO", "AR", "NJ", "PA"].some((item) =>
                        currentLocation.state.toUpperCase().includes(item)
                      )
                    : false;

                let apptTime;
                if (hasStateTimezone) {
                  apptTime = dayjs(appointment.time).tz(timezone);
                } else {
                  apptTime = dayjs(appointment.time);
                }

                return (
                  apptTime.format("YYYY-MM-DD") ===
                    selectedDate.format("YYYY-MM-DD") &&
                  apptTime.hour() === slotTime.hour() &&
                  apptTime.minute() === slotTime.minute()
                );
              });

              return (
                <div
                  key={`${hour}:${mins}`}
                  className={`flex items-start mb-2 border-t border-b border-[#E4E4E4] ${
                    mins === `30` ? "bg-[#E4E4E4]/20" : ""
                  }`}
                >
                  <div className="w-24 text-right pr-4 text-gray-500 text-sm pt-2">
                    {formatSlotTime(slotTime, currentLocation.state)}
                  </div>

                  <div className={`flex-1`}>
                    {slotAppointments.length > 0 ? (
                      slotAppointments.map((appointment, idx) => (
                        <>
                          <ScheduleCard
                            setExtendedAppointment={setExtendedAppointment}
                            setRescheduledAppointment={
                              setRescheduledAppointment
                            }
                            key={appointment.id || idx}
                            time={formatAppointmentTime(
                              appointment.time,
                              currentLocation.state
                            )}
                            name={
                              appointment.first_name +
                              " " +
                              appointment.last_name
                            }
                            duration={appointment.duration}
                            weight={
                              appointment.takeback?.rawTakeback?.data?.weight ||
                              appointment.weight ||
                              "unknown"
                            }
                            originalEstimatedWeight={
                              appointment.takeback?.rawTakeback?.data
                                ?.originalEstimatedWeight ||
                              appointment.takeback?.rawTakeback?.data?.weight ||
                              appointment.weight ||
                              "unknown"
                            }
                            isPast={dayjs(appointment.time).isBefore(dayjs())}
                            id={appointment.id}
                            firstName={appointment.first_name}
                            lastName={appointment.last_name}
                            takebackId={appointment.sys_id}
                            takeback={appointment.takeback}
                            appointment={appointment}
                            onDataChange={fetchAndJoinData}
                          />
                        </>
                      ))
                    ) : (
                      <div className="h-20" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
      <BottomBar />
      {rescheduledAppointment && (
        <Reschedule
          close={(booking) => {
            if (booking && booking.time) {
              setRescheduledAppointment(false);
            } else {
              setRescheduledAppointment(false);
            }
            setTimeout(() => {
              fetchAndJoinData();
            }, 1000);
          }}
          id={rescheduledAppointment.id}
        />
      )}
      {extendedAppointment && (
        <Extend
          date={extendedAppointment.time}
          close={() => {
            // Refresh data to get the updated appointment information
            setExtendedAppointment(null);
            setTimeout(() => {
              fetchAndJoinData();
            }, 1000);
          }}
          id={extendedAppointment.id}
        />
      )}
    </div>
  );
}
